import type { ReactNode } from "react";
import type { LearnerViewInPlay } from "@pushback-and-chaos/contracts";
import { PROPOSED } from "../../design/copy.js";
import type { ThreadEntry } from "../../data/adapt.js";
import type { NarrationAudioControl } from "../../ui/narration-audio.js";
import type { PageItem } from "./pages.js";
import { chapterHeadings, headingForNext } from "./chapter-heads.js";

/**
 * THE CONVERSATION, as atoms on pages.
 *
 * The thread is flattened into ATOMS — a chapter's heading, a line the
 * learner said, a paragraph of narration, a paper arriving, a tell, the opt-in
 * mechanics line — and `splitPages` (pages.tsx) decides where the pages fall:
 * a new page at every new in-game DAY, headed, and otherwise only when a page
 * already holds a chapter's worth. Every turn is an exchange (`anchor`), which
 * is what a page may begin at and what "the newest thing" is.
 *
 * THE DAY IS THE CHAPTER (Andre, 2026-10-29; before, the beat, 2026-10-02).
 * The server says which in-game day each turn ended on, as a coded slug line
 * ("Monday, April 6, 8:40 a.m."); a turn sits under the day it began on, the day the
 * one before it ended on (chapter-heads.ts, the rule the casebook shares), so
 * the learner's own line that crossed into a new day stays on the day it was
 * said, and the next turn opens the new day under its heading. A thread whose
 * turns carry no chapter (a server that does not say, a case missing from the
 * registry) — or a session with no turns yet — draws no heading at all: the
 * stream's beat is never drawn.
 *
 * Every string here comes from exactly where it came from before this layout
 * existed. Nothing is summarised, re-ordered or re-worded on the way in: this
 * file changes where the words sit, never what they say.
 */

export function storyAtoms(props: {
  readonly view: LearnerViewInPlay;
  readonly thread: readonly ThreadEntry[];
  readonly pendingText: string | null;
  readonly mentorAsk: string | null;
  readonly npcTells: readonly string[];
  readonly statuses: readonly string[];
  readonly busy: boolean;
  readonly voice: NarrationAudioControl | null;
}): readonly PageItem[] {
  const atoms: PageItem[] = [];
  const push = (item: PageItem): void => { atoms.push(item); };

  /** The days the turns ended on, and so the heading (if any) above each turn: the day it began on. */
  const ended = props.thread.map((entry) => entry.chapter);
  const headings = chapterHeadings(ended);
  const chapterHead = (key: string, title: string, breakBefore: boolean): PageItem => ({
    key,
    kind: "block",
    breakBefore,
    anchor: true,
    node: () => <h2 className="pcs-story-title">{title}</h2>,
  });

  if (props.thread.length > 0) {
    for (const [index, entry] of props.thread.entries()) {
      const opening = index === 0 && entry.learnerText === null && entry.narration.length > 0;
      const title = headings[index] ?? null;
      const opensChapter = title !== null;
      if (opensChapter) push(chapterHead(`${entry.key}:chapter`, title, index > 0));
      /** The first atom of a turn begins the exchange, unless a chapter head already did. */
      let anchored = opensChapter;
      const first = <T extends PageItem>(item: T): T => {
        if (anchored) return item;
        anchored = true;
        return { ...item, anchor: true };
      };

      if (entry.learnerText !== null) {
        push(first({ key: `${entry.key}:said`, kind: "text", text: entry.learnerText, className: "pcs-said" }));
      }
      for (const [i, line] of entry.narration.entries()) {
        push(first({ key: `${entry.key}:n${String(i)}`, kind: "text", text: line, className: "pcs-prose" }));
      }
      if (props.voice !== null && entry.narration.length > 0) {
        const voice = props.voice;
        push({ key: `${entry.key}:listen`, kind: "block", node: () => <ListenControl entryKey={entry.key} voice={voice} /> });
      }
      // The mentor's opening ask, under the authored opening, in his voice
      // (onboarding pass, 2026-09-13). Only ever on the opening entry.
      if (opening && props.mentorAsk !== null) {
        push({ key: `${entry.key}:ask`, kind: "text", text: props.mentorAsk, className: "pcs-prose", tail: <span data-testid="mentor-ask" /> });
      }
      // Turn 0's authored opening paragraph replaces the per-document arrival
      // lines (ratified decision 9.3); arrivals stay for every later turn.
      if (!opening) {
        for (const [i, doc] of entry.documents.entries()) {
          push({
            key: `${entry.key}:d${String(i)}`,
            kind: "block",
            node: () => (
              <p className="pcs-aside">
                <i>{doc.title}</i> lands on the desk.
              </p>
            ),
          });
        }
      }
      for (const [i, tell] of entry.npcTells.entries()) {
        push(first({ key: `${entry.key}:t${String(i)}`, kind: "text", text: tell, className: "pcs-aside" }));
      }
      // The opt-in mechanics line, from the record. ONE renderer, here.
      if (entry.mechanics !== null) {
        const mechanics = entry.mechanics;
        push({
          key: `${entry.key}:mech`,
          kind: "block",
          node: () => (
            <p data-testid="turn-mechanics" className="pcs-mechanics">
              {mechanics}
            </p>
          ),
        });
      }
    }
  } else {
    // No event log yet (a session that has taken no turn): the bare narration
    // list, exactly as the old surface fell back to it.
    if (props.view.narration.length === 0 && !props.busy) {
      push({
        key: "empty",
        kind: "text",
        text: "The weekend is yours. The desk has what Hugh sent. Ask something, ask for something, or say what you think is going on.",
        className: "pcs-aside",
      });
    }
    for (const [index, line] of props.view.narration.entries()) {
      push({ key: `open:n${String(index)}`, kind: "text", text: String(line), className: "pcs-prose", anchor: index === 0 });
    }
    for (const [i, tell] of props.npcTells.entries()) {
      push({ key: `open:t${String(i)}`, kind: "text", text: tell, className: "pcs-aside" });
    }
  }

  if (props.pendingText !== null) {
    // The line being worked begins on the day the last turn ended on. When
    // it lands as the next turn it is placed by exactly the same rule, so it
    // does not move.
    const pendingTitle = headingForNext(ended);
    const pendingOpens = pendingTitle !== null;
    if (pendingOpens) push(chapterHead("pending:chapter", pendingTitle, true));
    push({ key: "pending", kind: "text", text: props.pendingText, className: "pcs-said", anchor: !pendingOpens });
  }

  const status = props.statuses.at(-1);
  if (status !== undefined) {
    push({ key: "status", kind: "text", text: status, className: "pcs-aside" });
  }

  if (props.busy) {
    push({
      key: "working",
      kind: "block",
      node: () => (
        <p className="pcs-aside">
          <span className="inline-block animate-pulse">Somebody is thinking about it.</span>
        </p>
      ),
    });
  }

  return atoms;
}

/**
 * One turn's listen control (ADR 0011): a word under the narration, in the
 * faint ink of the panel's own labels, that says what the voice is doing.
 */
function ListenControl({ entryKey, voice }: { entryKey: string; voice: NarrationAudioControl }): ReactNode {
  const playing = voice.playingKey === entryKey;
  const loading = voice.loadingKey === entryKey;
  const failed = voice.failedKey === entryKey;
  const label = playing ? PROPOSED.stopListening : loading ? PROPOSED.fetchingVoice : failed ? PROPOSED.voiceFailed : PROPOSED.listen;
  return (
    <button
      type="button"
      onClick={() => { voice.play(entryKey); }}
      disabled={loading}
      aria-label={PROPOSED.listenLabel}
      aria-pressed={playing}
      data-listen={playing ? "playing" : loading ? "loading" : failed ? "failed" : "idle"}
      className={[
        "-mt-2 mb-4 block border-0 bg-transparent p-0 text-left font-sans text-micro tracking-wide",
        loading ? "cursor-wait text-ink-faint" : "cursor-pointer text-ink-faint hover:text-ink",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
